import { getI18n } from '@/plugins/i18n'
import * as dataGrid from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'

import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useAccountingStore } from '@/stores/accounting.store'

import { useValidation } from '@/composables/validation'
import { useSubjectService } from '@/services/subject.service'

export const useColumns = (
  editors,
  currentCountryId = {},
  currentStateId = {},
  currentVatCountryId = {},
  currentCertifiedEmail = {},
  currentVerifyCertifiedEmail = {},
  currentSubjectEntityType = {},
  currentSubjectType = {},
  currentSubjectTypeId = {},
  currentSubjectSubTypeId = {},
  currentTradeAccountId = {},
  currentFinancialCenterId = {},
  currentVatCountryCode = {},
  currentFilteredCitiesCount,
  getFilteredTradeAccounts,
  getFilteredFinancialCenters,
  updateTabPanelVisibility,
  setDefaultCustomerData,
  setCellValueZipCode,
  isCustomer,
  isSupplier,
  privacyRequired = {},
) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()
  const webSiteStore = useWebSiteStore()
  const accountingStore = useAccountingStore()

  const { validateVatNumber, validateRequiredVatNumber, validateTaxCode } = useValidation()
  const { setSubjectType, isVatCountryIta, isNameExtMandatory } = useSubjectService()
  const isPublicAdministration = () => registryStore.subjectTypes.find(subjectType => subjectType.id === currentSubjectSubTypeId.value)?.code === 'EPA'

  const getSubjectTypes = (options) => {
    return {
      store: registryStore.getLocalizedSubjectTypes(),
      filter: ['parent_id', '=', null],
      sort: 'name',
    }
  }
  const getSubjectSubTypes = (option) => {
    return {
      store: registryStore.getLocalizedSubjectTypes(),
      filter: ['parent_id', '=', currentSubjectTypeId.value ? currentSubjectTypeId.value : -1],
      sort: 'name',
    }
  }

  return [
    {
      type: 'buttons',
      buttons: [{template: 'editButtonsTemplate'}],
      width: '4rem',
      visibleIndex: 0,
      fixed: false
    },
    dataGridColumn.useAttachmentColumn(),
    {
      dataField: 'type',
      caption: i18n.t('common.subjectSubType'),
      cellTemplate: 'typeCellTemplate',
      alignment: 'center',
      width: '8rem',
      lookup: dataGrid.useLookup({ store: registryStore.getLocalizedSubjectEntityTypes(), displayExpr: 'name' }),
      setCellValue: (newData, value) => {
        newData.type = value
        currentSubjectEntityType.value = value

        if (isCustomer(newData)) { setDefaultCustomerData(newData) }
        updateTabPanelVisibility()
      },
      validationRules: [{ type: 'required' }],
    },
    { dataField: 'code', caption: i18n.t('common.code'), width: '4rem', validationRules: [{ type: 'required' }] },
    {
      dataField: 'customer_privacy_check',
      cellTemplate: 'privacyCheckCellTemplate',
      caption: i18n.t('common.privacyConsent'),
      width: '6rem',
    },
    {
      dataField: 'is_partial',
      cellTemplate: 'isPartialCellTemplate',
      visible: false,
      caption: i18n.t('common.partial'),
    },
    { dataField: 'name', caption: `${i18n.t('common.nameAlt')} / ${i18n.t('common.lastName')}`, sortOrder: 'asc', validationRules: [{ type: 'required' }] },
    {
      dataField: 'name_ext',
      caption: `${i18n.t('common.nameExtAlt')} / ${i18n.t('common.firstName')}`,
      validationRules: [{
        type: 'custom',
        reevaluate: true,
        message: i18n.t('common.required'),
        validationCallback: (e) => !isNameExtMandatory(e.data?.subject_type_ref) || !!e.value,
      }],
    },
    { dataField: 'phone', caption: i18n.t('common.phone') },
    { dataField: 'phone_ext', caption: i18n.t('common.phoneExt') },
    { dataField: 'mobile', caption: i18n.t('common.mobile') },
    { dataField: 'email', caption: i18n.t('common.email'), validationRules: [{ type: 'email' }] },
    { dataField: 'email_ext', caption: i18n.t('common.emailExt'), validationRules: [{ type: 'email' }] },
    { dataField: 'email_certified', caption: i18n.t('common.emailCertified'), validationRules: [{ type: 'email' }] },
    {
      dataField: 'vat_number',
      caption: i18n.t('common.vatNumber'),
      validationRules: [{
        type: 'custom',
        message: i18n.t('common.required'),
        reevaluate: true,
        validationCallback: (e) => validateRequiredVatNumber(e.value, currentVatCountryId.value, currentSubjectTypeId.value)
      },
      {
        type: 'custom',
        reevaluate: true,
        validationCallback: (e) => !registryStore.companyData.subject_validate_vat_and_fiscal_code || validateVatNumber(e.value, currentVatCountryId.value),
      },
      ]
    },
    dataGridColumn.useTaxCodeColumn({
      validationRules: [
      {
        type: 'custom',
        reevaluate: true,
        validationCallback: (e) => !registryStore.companyData.subject_validate_vat_and_fiscal_code || validateTaxCode(e.value, currentVatCountryCode.value),
      }, {
        type: 'required'
      }]
    }),
    {
      dataField: 'address.country_name', caption: i18n.t('common.country'),
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'address.country',
      editCellTemplate: 'countryEditor',
      setCellValue: (newData, value, currentData) => {
        newData.address = {}
        newData.address.country = currentCountryId.value = value?.id
        newData.address.state = currentStateId.value = null
        newData.address.city = null
        newData.address.zip_code = null
        newData.address.state_text = null
        newData.address.city_text = null
        newData.address.street = null
      },
      validationRules: [{ type: 'required' }],
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'address.state_name', caption: i18n.t('common.state'),
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'address.state',
      editCellTemplate: 'stateEditor',
      setCellValue: (newData, value, currentData) => {
        newData.address = {}
        newData.address.state = newData.address.state_text = newData.address.city = newData.address.street = null

        const actualAddress = {...currentData.address, ...newData.address}

        const currentCountry = webSiteStore.countries.find(x => x.id === actualAddress?.country)
        if (currentCountry?.has_structured_location_data) {
          newData.address.state = currentStateId.value = value?.id
        } else {
          newData.address.state_text = value
        }
      },
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'address.city_name', caption: i18n.t('common.city'),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'address.city',
      editCellTemplate: 'cityEditor',
      setCellValue: (newData, value, currentData) => {
        newData.address = {}
        newData.address.city = newData.address.city_text = null

        const actualAddress = {...currentData.address, ...newData.address}

        const currentCountry = webSiteStore.countries.find(x => x.id === actualAddress?.country)
        if (currentCountry?.has_structured_location_data && (currentFilteredCitiesCount.value == null || currentFilteredCitiesCount.value > 0)) {
          newData.address.state = currentStateId.value = value?.state
          newData.address.city = value?.id
          newData.address.zip_code = value?.zip_code
        } else {
          newData.address.city_text = value
        }
      },
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('common.required'),
          reevaluate: true,
          validationCallback: (e) => {
            return e.value !== null || e.data.address.city_text
          }
        }
      ],
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'subject_type_ref',
      caption: i18n.t('common.personType'),
      lookup: dataGrid.useLookup({ dataSource: getSubjectTypes }),
      setCellValue: (newData, value, currentData) => {
        newData = newData || {}
        currentSubjectTypeId.value = value
        currentSubjectSubTypeId.value = null
        setSubjectType(newData, currentData, value)
      },
      validationRules: [{ type: 'required' }],
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'subject_sub_type_ref',
      caption: `${i18n.t('common.category')} / ${i18n.t('common.gender')}`,
      lookup: dataGrid.useLookup({ dataSource: getSubjectSubTypes }),
      setCellValue: (newData, value, currentData) => {
        newData = newData || {}
        currentSubjectSubTypeId.value = value
        currentSubjectTypeId.value = currentSubjectType.value.parent_ref
        setSubjectType(newData, currentData, currentData.subject_type_ref, value)
      },
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'address.street', caption: i18n.t('common.street'), validationRules: [{ type: 'required' }],
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'address.zip_code',
      caption: i18n.t('common.zipCode'),
      validationRules: [{ type: 'required' }],
      visible: false,
      showInColumnChooser: false,
      setCellValue: (newData, value, currentData) => setCellValueZipCode(newData, value, currentData, null)
    },
    {
      dataField: 'vat_country_id',
      editCellTemplate: 'countryEditor',
      caption: i18n.t('common.vatCountry'),
      setCellValue: (newData, value, currentData) => {
        newData.vat_country_id = currentVatCountryId.value = value?.id || null
        editors['vat_number'].option('disabled', !isVatCountryIta(newData.vat_country_id))
      },
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'customer.birth_country',
      editCellTemplate: 'birthCountryEditor',
      setCellValue: (newData, value, currentData) => {
        newData.customer = currentData.customer || {}
        newData.customer.birth_country = value?.id
        newData.customer.birth_place = null
        newData.customer.birth_city = null
      },
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'customer.birth_city', caption: i18n.t('common.birthCity'),
      editCellTemplate: 'birthCityEditor',
      setCellValue: (newData, value, currentData) => {
        newData.customer = currentData.customer || {}
        const birthCountry = webSiteStore.countries.find(x => x.id === newData.customer.birth_country)
        if (birthCountry?.has_structured_location_data && value && typeof value === 'object') {
          newData.customer.birth_city = value.name
          const birthState = webSiteStore.states.find(s => s.id === value.state)
          if (birthState) {
            newData.customer.birth_place = birthState.code
          }
        } else {
          newData.customer.birth_city = value || null
        }
      },
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'customer.birth_place', caption: i18n.t('common.birthState'),
      editCellTemplate: 'birthStateEditor',
      setCellValue: (newData, value, currentData) => {
        newData.customer = currentData.customer || {}
        const birthCountry = webSiteStore.countries.find(x => x.id === newData.customer.birth_country)
        if (birthCountry?.has_structured_location_data && value && typeof value === 'object') {
          newData.customer.birth_place = value.code
          newData.customer.birth_city = null
        } else {
          newData.customer.birth_place = value || null
        }
      },
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer.birth_date', caption: i18n.t('common.birthDate'), dataType: 'date',
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer.job_type_ref',
      caption: i18n.t('common.jobType'),
      lookup: dataGrid.useLookup({
        store: registryStore.getLocalizedJobTypes().map(i => ({ sort_code: i.code.padStart(2, '0'), ...i })),
        sort: 'sort_code',
      }),
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'customer.social_link', caption: i18n.t('common.socialLink'),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer.marketing_communications_consent',
      caption: i18n.t('common.marketingCommunicationsConsent'),
      dataType: 'boolean',
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer.third_party_data_transfer_consent',
      caption: i18n.t('common.thirdPartyDataTransferConsent'),
      dataType: 'boolean',
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer.privacy_check',
      caption: i18n.t('common.privacyChecked'),
      dataType: 'boolean',
      validationRules: [{
        type: 'custom',
        message: i18n.t('error.validation.privacy_check'),
        reevaluate: true,
        validationCallback: (e) => !privacyRequired.value || e.value === true,
      }],
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'notes', caption: i18n.t('common.note', 2),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer.identity_document_type_ref',
      caption: i18n.t('common.identityDocumentType'),
      lookup: dataGrid.useLookup({ store: registryStore.getLocalizedIdentityDocumentTypes() }),
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'customer.identity_document_number', caption: i18n.t('common.identityDocumentNumber'),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer.identity_document_authority_ref',
      caption: i18n.t('common.identityDocumentAuthority'),
      lookup: dataGrid.useLookup({ store: registryStore.getLocalizedIdentityDocumentAuthorities() }),
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'customer.identity_document_date', caption: i18n.t('common.identityDocumentDate'), dataType: 'date',
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer.identity_document_city', caption: i18n.t('common.identityDocumentCity'),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer.identity_document_state', caption: i18n.t('common.identityDocumentState'),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer.buyer_type_ref',
      caption: i18n.t('common.buyerType'),
      lookup: dataGrid.useLookup({ store: registryStore.getLocalizedBuyerTypes() }),
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'customer.channel_ref',
      caption: i18n.t('common.channel'),
      lookup: dataGrid.useLookup({ store: registryStore.getLocalizedChannels() }),
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'customer.channel_type_ref',
      caption: i18n.t('common.channelType'),
      lookup: dataGrid.useLookup({ store: registryStore.getLocalizedChannelTypes() }),
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'customer.customer_type_ref',
      caption: i18n.t('common.customerType'),
      lookup: dataGrid.useLookup({ store: registryStore.getLocalizedCustomerTypes() }),
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'customer.discount_list_ref',
      caption: i18n.t('common.discountGroup'),
      lookup: dataGrid.useLookup({ store: registryStore.getLocalizedDiscountLists() }),
      validationRules: [{
        type: 'custom',
        message: i18n.t('common.required'),
        validationCallback: (e) => { return !isCustomer(e.data) || e.value != null }
      }],
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'customer_accounting.customer_accounting_status_type',
      caption: i18n.t('common.status'),
      lookup: dataGrid.useLookup({ store: accountingStore.getLocalizedCustomerAccountingStatusTypesEnum(), displayExpr: 'name' }),
      validationRules: [{
        type: 'custom',
        message: i18n.t('common.required'),
        validationCallback: (e) => { return !isCustomer(e.data) || e.value != null }
      }],
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_accounting.vat_type_ref',
      caption: i18n.t('common.accountingVat'),
      lookup: dataGrid.useLookup({ store: webSiteStore.getLocalizedVatTypes() }),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_accounting.payment_type_ref',
      caption: i18n.t('common.paymentType'),
      lookup: dataGrid.useLookup({ store: webSiteStore.getLocalizedPaymentTypes() }),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_accounting.currency_ref',
      caption: i18n.t('common.currency'),
      lookup: dataGrid.useLookup({ store: webSiteStore.getLocalizedCurrencies() }),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_accounting.single_invoice',
      caption: i18n.t('common.invoiceGrouping'),
      dataType: 'boolean',
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_accounting.riba_grouping',
      caption: i18n.t('common.ribaGrouping'),
      dataType: 'boolean',
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_accounting.is_stamp_duty_customer',
      caption: i18n.t('common.isStampDutyCustomer'),
      dataType: 'boolean',
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_accounting.remind_packaging',
      caption: i18n.t('common.remindPackaging'),
      dataType: 'boolean',
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_accounting.remind_insurance',
      caption: i18n.t('common.remindInsurance'),
      dataType: 'boolean',
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_accounting.remind_payment_collection',
      caption: i18n.t('common.remindPaymentCollection'),
      dataType: 'boolean',
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_accounting.iban',
      caption: i18n.t('common.iban'),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_accounting.swift_code',
      caption: i18n.t('common.swiftCode'),
      visible: false,
      showInColumnChooser: false
    },
    dataGridColumn.useCustomerColumn({
      dataField: 'customer_accounting.invoice_to_customer',
      caption: i18n.t('common.invoiceToCustomer'),
      editCellTemplate: 'customerEditor',
      setCellValue: (newData, value) => {
        newData.customer_accounting = newData.customer_accounting || {}
        newData.customer_accounting.invoice_to_customer = value?.id || null
        newData.customer_accounting.accounting_code = value?.code || null
      },
      validationRules: [],
    }),
    {
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_accounting.master_account_code',
      caption: i18n.t('common.customerMasterAccountCode'),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_accounting.accounting_code',
      caption: i18n.t('common.accountingCode'),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_accounting.do_not_export',
      caption: i18n.t('common.doNotExport'),
      dataType: 'boolean',
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_electronic_invoice.agreement_type_ref',
      caption: i18n.t('common.agreementType'),
      lookup: dataGrid.useLookup({ store: registryStore.getLocalizedCustomerElectronicInvoiceAgreementTypes()}),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_electronic_invoice.agreement_type_code',
      caption: i18n.t('common.agreementCode'),
      visible: false,
      showInColumnChooser: false},
    {
      dataField: 'customer_electronic_invoice.email_certified',
      caption: i18n.t('common.emailCertified'),
      validationRules: [{ type: 'email' }, {
        type: 'custom',
        message: i18n.t('common.emailsDoNotMatch'),
        validationCallback: (e) => { return !e.value || !currentVerifyCertifiedEmail.value || e.value === currentVerifyCertifiedEmail.value }
      }],
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_electronic_invoice.verify_email_certified',
      caption: i18n.t('common.emailCertifiedVerify'),
      validationRules: [{ type: 'email' }, {
        type: 'custom',
        message: i18n.t('common.emailsDoNotMatch'),
        validationCallback: (e) => { return !e.value || !currentCertifiedEmail.value || e.value === currentCertifiedEmail.value }
      }],
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_electronic_invoice.sdi_code',
      caption: i18n.t('common.sdiCode'),
      validationRules: [
        {
          type: 'custom',
          reevaluate: true,
          message: i18n.t('error.validation.too_long', { max: 6 }),
          validationCallback: (e) => !isPublicAdministration() || !e.value || e.value.length <= 6,
        },
        {
          type: 'custom',
          reevaluate: true,
          message: i18n.t('error.validation.too_long', { max: 7 }),
          validationCallback: (e) => isPublicAdministration() || !e.value || e.value.length <= 7,
        },
      ],
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_electronic_invoice.administrative_reference',
      caption: i18n.t('common.administrativeReference'),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_electronic_invoice.split_payment',
      caption: i18n.t('common.splitPayment'),
      dataType: 'boolean',
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_electronic_invoice.protocol',
      caption: i18n.t('common.protocol'),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_electronic_invoice.serial_number',
      caption: i18n.t('common.serialNumber'),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_electronic_invoice.declaration_date',
      caption: i18n.t('common.declarationDate'),
      dataType: 'date',
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'customer_electronic_invoice.expiration_date',
      caption: i18n.t('common.expirationDate'),
      dataType: 'date',
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'supplier.supplier_type_ref',
      caption: i18n.t('common.supplierType'),
      dataType: 'number',
      lookup: dataGrid.useLookup({ store: registryStore.getLocalizedSupplierTypes() }),
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useSupplierBrandColumn({
      dataField: 'supplier.supplier_brand_ref',
      setCellValue: (newData, value) => {
        newData.supplier = newData.supplier || {}
        newData.supplier.supplier_brand_ref = value
      },
      validationRules: [{
        type: 'custom',
        message: i18n.t('common.required'),
        validationCallback: (e) => { return !isSupplier(e.data) || e.value != null }
      }],
    }),
    {
      dataField: 'supplier.transmission_link_code',
      caption: i18n.t('common.transmissionLinkCode'),
      validationRules: [{ type: 'stringLength', max: 50 }],
      setCellValue: (newData, value) => {
        newData.transmission_link_code = value
      },
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'supplier.transmission_company_code',
      caption: i18n.t('common.transmissionCompanyCode'),
      validationRules: [{ type: 'stringLength', max: 50 }],
      setCellValue: (newData, value) => {
        newData.transmission_company_code = value
      },
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'supplier.transmission_internal_code',
      caption: i18n.t('common.transmissionInternalCode'),
      validationRules: [{ type: 'stringLength', max: 50 }],
      setCellValue: (newData, value) => {
        newData.transmission_internal_code = value
      },
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'supplier.transmission_code',
      caption: i18n.t('common.transmissionCode'),
      validationRules: [{ type: 'stringLength', max: 50 }],
      setCellValue: (newData, value) => {
        newData.transmission_code = value
      },
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'supplier_accounting.trade_account_ref',
      caption: i18n.t('common.tradeAccount'),
      lookup: dataGrid.useLookup({ dataSource: getFilteredTradeAccounts }),
      setCellValue: (newData, value) => {
        newData = newData || {}
        newData.supplier_accounting = newData.supplier_accounting || {}
        newData.supplier_accounting.trade_account_ref = value
        currentTradeAccountId.value = value
        if (value && currentFinancialCenterId.value) {
          if (!accountingStore.getFinancialCenterIdsByTradeAccountId(value).includes(currentFinancialCenterId.value)) {
            currentFinancialCenterId.value = null
            newData.supplier_accounting.financial_center_ref = null
          }
        }
      },
      showInColumnChooser: false
    },
    {
      dataField: 'supplier_accounting.financial_center_ref',
      caption: i18n.t('common.financialCenter'),
      lookup: dataGrid.useLookup({ dataSource: getFilteredFinancialCenters }),
      setCellValue: (newData, value) => {
        newData = newData || {}
        newData.supplier_accounting = newData.supplier_accounting || {}
        newData.supplier_accounting.financial_center_ref = value
        currentFinancialCenterId.value = value
        if (value && currentTradeAccountId.value) {
          if (!accountingStore.getTradeAccountIdsByFinancialCenterId(value).includes(currentTradeAccountId.value)) {
            currentTradeAccountId.value = null
            newData.supplier_accounting.trade_account_ref = null
          }
        }
      },
      showInColumnChooser: false
    },
    {
      dataField: 'supplier_accounting.master_account_code',
      caption: i18n.t('common.supplierMasterAccountCode'),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'supplier_accounting.accounting_code',
      caption: i18n.t('common.accountingCode'),
      visible: false,
      showInColumnChooser: false
    },
    {
      dataField: 'supplier_accounting.do_not_export',
      caption: i18n.t('common.doNotExport'),
      dataType: 'boolean',
      visible: false,
      showInColumnChooser: false
    },
  ]
}
